/**
 * Image URL validation utilities
 */

/**
 * Check if an image URL is valid for web use
 * Filters out local file paths and invalid URLs
 */
export function isValidImageUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  // Reject local file paths (Android/iOS cache paths)
  if (trimmed.startsWith('/data/') ||
      trimmed.startsWith('/storage/') ||
      trimmed.startsWith('/var/') ||
      trimmed.startsWith('/private/') ||
      (trimmed.includes('cache/') && trimmed.startsWith('/'))) {
    return false;
  }

  // Must be a proper HTTP/HTTPS URL or relative path
  return trimmed.startsWith('http://') ||
         trimmed.startsWith('https://') ||
         trimmed.startsWith('/');
}

/**
 * Get a valid image URL, with fallback options
 */
export function getValidImageUrl(
  primaryUrl: string | null | undefined,
  fallbackUrls: (string | null | undefined)[] = []
): string | undefined {
  // Check primary URL first
  if (isValidImageUrl(primaryUrl)) {
    return primaryUrl || undefined;
  }

  // Check fallback URLs
  for (const fallback of fallbackUrls) {
    if (isValidImageUrl(fallback)) {
      return fallback || undefined;
    }
  }

  return undefined;
}

/**
 * Sanitize and validate product images
 * Removes invalid local paths and returns only valid URLs
 */
export function sanitizeProductImages(images: (string | null | undefined)[]): string[] {
  return images
    .filter(url => isValidImageUrl(url))
    .map(url => url!.trim());
}

// Explicitly mark this file as a module
export {};